import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { seriesColor } from '../constants/chartPalette';
import {
  ChartDataPoint,
  colourForToken,
  conicStops,
  enterAt,
  formatRatio,
  formatValue,
  percentOf,
  WidgetPeriodChip,
} from '../shared';

/** Ring diameter. The legend takes the rest of the row. */
const RING_PX = 100;

/**
 * Composition — the parts of a whole.
 *
 * `donut` opens a hole and prints the total in it. `innerRadiusPct` is the
 * contract's `display.inner_radius_pct`, and it has a floor in practice: the
 * widest line of centre text spans the hole's *inscribed square*, which is
 * only 71% of its diameter, so a hole sized to the text ends up with the text
 * touching the ring on both sides.
 *
 * Slices are drawn in the order given and are **not** re-sorted or normalised:
 * a breakdown that does not sum to the whole draws a gap, which is the honest
 * rendering of incomplete data. Pass `total` when the absolute figure differs
 * from the sum of the slices — a spend breakdown whose "Other" bucket is
 * rolled up, say.
 *
 * Each legend row carries the slice's **value and its share** — `98  27.5%`.
 * Either alone is half the answer: a percentage with no quantity cannot be
 * turned back into one (the total is in the middle of a ring, not in the
 * reader's head), and a quantity with no percentage does not say how much of
 * the whole it is, which is the only thing a composition chart exists for.
 *
 * 24px between ring and legend, not 16: at 16 the first swatch sits inside the
 * ring's optical edge and the two read as one object.
 *
 * @param {object} props
 * @param {{ slices: { label: string, value: number, display?: string }[], total?: number, totalLabel?: string, unit?: string }} props.data
 * @param {boolean} [props.donut=false] Open the centre and print the total.
 * @param {number} [props.innerRadiusPct=62] The hole, as a share of the ring.
 * @param {string} [props.accent] Overrides the first slice's colour.
 * @param {string} [props.className]
 */
function PieChartWidget({ data, donut = false, innerRadiusPct = 62, accent, className = '' }) {
  /*
    Three types, one drawing (CT-5641).

    `PIE_CHART` and `DONUT` publish `slices[]`; `SPEND_BREAKDOWN` publishes
    `items[]` with the same `{key, label, value, pct}` and an optional
    `children[]` of sub-categories. They are read as one list here, and the
    sub-categories are shown under their parent rather than flattened into it —
    a breakdown that loses its second level is a pie with extra steps.

    `pct` comes from the contract. The widget no longer divides by the sum,
    because the sum it can see is only the slices it was sent: a chart capped
    at ten categories plus an «others» row would otherwise compute percentages
    of the visible ten and quietly disagree with the total beside it.
  */
  const slices = useMemo(() => {
    const raw = data?.slices ?? data?.items ?? [];
    return raw.map((slice, index) => ({
      key: slice.key ?? slice.label ?? String(index),
      label: slice.label ?? slice.key ?? '',
      value: slice.value,
      pct: slice.pct,
      isOthers: Boolean(slice.is_others),
      children: slice.children ?? [],
      color: slice.color_token
        ? colourForToken(slice.color_token, seriesColor(index, accent))
        : seriesColor(index, accent),
    }));
  }, [accent, data]);

  const format = data?.currency_code
    ? { style: 'CURRENCY', currency_code: data.currency_code, ...(data.format ?? {}) }
    : data?.format ?? null;

  const sum = useMemo(
    () => slices.reduce((acc, slice) => acc + (slice.value ?? 0), 0),
    [slices],
  );
  // The ring is drawn from the share of the visible total, which is what a
  // ring can show. The printed percentage is the contract's `pct`.
  const stops = useMemo(
    () => conicStops(slices.map((slice) => percentOf(slice.value, sum)), (index) => slices[index].color),
    [slices, sum],
  );

  if (!slices.length) return null;

  const headline = data?.total ?? sum;
  const totalLabel = data?.totalLabel ?? 'total';
  /*
    One decimal, not zero.

    Two slices at 16.8% and 17.4% both round to 17%, and a composition chart
    whose two middle rows read identically has lost the comparison it exists
    to make. The tenth is also what the contract's `pct` carries — rounding it
    away is discarding precision the backend computed.
  */
  const share = (slice) => (
    typeof slice.pct === 'number' ? formatRatio(slice.pct, { signed: false, decimals: 1 })
      : `${percentOf(slice.value, sum)}%`
  );

  return (
    <div className={`flex h-full items-center gap-6 ${className}`.trim()}>
      <div className="relative shrink-0" style={{ width: RING_PX, height: RING_PX }}>
        <div
          aria-hidden
          className="wg-enter-pop h-full w-full rounded-full"
          style={{ background: `conic-gradient(${stops})`, animationDuration: '500ms' }}
        />
        {donut ? (
          <div
            className="absolute flex flex-col items-center justify-center gap-0.5 rounded-full bg-surface"
            style={{ inset: `${(100 - innerRadiusPct) / 2}%` }}
          >
            <span className="text-sm font-bold leading-tight text-content">
              {formatValue(headline, format)}
            </span>
            <span className="text-[9px] leading-none text-content-muted">{totalLabel}</span>
          </div>
        ) : null}
      </div>

      <div className="min-w-0 flex-1 pr-1">
        {slices.map((slice, index) => {
          const enter = enterAt(index, 'left');

          return (
            <div key={slice.key}>
              <ChartDataPoint
                fill="block w-full"
                label={`${slice.label} · ${share(slice)} — ${formatValue(slice.value, format)} of ${formatValue(headline, format)}`}
              >
                <div
                  className={`flex items-center gap-2 py-1 ${enter.className}`}
                  style={enter.style}
                >
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-sm"
                    style={{ background: slice.color }}
                  />
                  <span
                    className={`min-w-0 flex-1 truncate text-[11px] ${slice.isOthers ? 'italic text-content-muted' : 'text-content-muted'}`}
                  >
                    {slice.label}
                  </span>
                  <span className="shrink-0 text-[11px] text-content-muted">
                    {formatValue(slice.value, format)}
                  </span>
                  <span className="w-10 shrink-0 text-right text-[11px] font-semibold text-content">
                    {share(slice)}
                  </span>
                </div>
              </ChartDataPoint>

              {/* Sub-categories, indented under their parent — SPEND_BREAKDOWN only. */}
              {slice.children.map((child) => (
                <div
                  key={child.key ?? child.label}
                  className="flex items-center gap-2 py-0.5 pl-4 text-[10px] text-content-muted"
                >
                  <span className="min-w-0 flex-1 truncate">{child.label}</span>
                  <span className="shrink-0">{formatValue(child.value, format)}</span>
                </div>
              ))}
            </div>
          );
        })}
        <WidgetPeriodChip period={data?.period} className="mt-1" />
      </div>
    </div>
  );
}

PieChartWidget.propTypes = {
  /** `PIE_CHART` / `DONUT`'s `slices[]`, or `SPEND_BREAKDOWN`'s `items[]`. */
  data: PropTypes.shape({
    slices: PropTypes.arrayOf(PropTypes.object),
    items: PropTypes.arrayOf(PropTypes.object),
    total: PropTypes.number,
    format: PropTypes.object,
    currency_code: PropTypes.string,
    period: PropTypes.object,
    totalLabel: PropTypes.string,
  }).isRequired,
  donut: PropTypes.bool,
  /** `display.inner_radius_pct`. Below ~50 the centre total starts clipping. */
  innerRadiusPct: PropTypes.number,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(PieChartWidget);
